import { Link } from "react-router-dom";
import { useSEO, breadcrumb } from "../lib/seo";
import { SITE } from "../lib/site";
import PageHero from "../components/PageHero";
import { LogIn, ShieldCheck, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";

const STEPS = [
  { num: "01", title: "Buka halaman resmi Sloto69", desc: "Akses melalui bookmark atau halaman link alternatif resmi. Hindari klik tautan dari sumber tidak dikenal." },
  { num: "02", title: "Pastikan alamat website tepat", desc: "Periksa ejaan domain, ikon gembok, dan konsistensi tampilan halaman dengan dokumentasi resmi." },
  { num: "03", title: "Klik tombol Masuk / Login", desc: "Tombol biasanya tersedia di bagian atas atau bagian utama halaman. Klik dengan tenang." },
  { num: "04", title: "Masukkan informasi akun dengan teliti", desc: "Periksa setiap karakter sebelum mengirim. Hindari menggunakan jaringan publik tanpa pengamanan tambahan." },
  { num: "05", title: "Selesaikan verifikasi (jika diminta)", desc: "Beberapa proses login meminta verifikasi tambahan. Ikuti instruksi resmi dengan saksama." },
  { num: "06", title: "Logout setelah selesai", desc: "Khususnya saat menggunakan perangkat umum atau bersama, biasakan logout untuk menjaga privasi." },
];

const TIPS = [
  "Aktifkan kebiasaan memeriksa alamat URL.",
  "Hindari menyimpan kata sandi di peramban perangkat umum.",
  "Gunakan kata sandi yang kuat dan unik.",
  "Jangan membagikan kredensial kepada siapa pun.",
];

export default function LoginGuide() {
  useSEO({
    title: "Panduan Login Sloto69 | Cara Masuk dengan Aman",
    description: "Panduan login Sloto69 yang aman dan mudah dipahami: dari memeriksa alamat resmi hingga praktik terbaik setelah login.",
    keywords: "sloto69 login, panduan login sloto69, cara login sloto69 aman",
    canonical: `${SITE.baseUrl}/panduan-login`,
    jsonLd: breadcrumb([
      { name: "Beranda", url: `${SITE.baseUrl}/` },
      { name: "Panduan Login", url: `${SITE.baseUrl}/panduan-login` },
    ]),
  });

  return (
    <>
      <PageHero
        eyebrow="Panduan Login"
        title="Cara Login Sloto69 dengan Aman dan Mudah"
        description="Pahami langkah login Sloto69 secara bertahap, lengkap dengan tips menjaga keamanan akun selama proses akses."
        breadcrumb={[{ label: "Panduan Login" }]}
      />

      <section className="section-pad" data-testid="login-steps">
        <div className="shell grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="gold-pill">Langkah Demi Langkah</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              6 Langkah Login Sloto69
            </h2>
            <p className="mt-4 text-slate-300">
              Ikuti langkah berikut untuk memastikan proses login berjalan aman dan nyaman.
            </p>
            <a href={SITE.loginUrl} data-testid="loginguide-cta" className="btn-primary mt-6 inline-flex">
              <LogIn className="w-4 h-4" /> Masuk Sloto69
            </a>
          </div>

          <ol className="lg:col-span-8 space-y-4">
            {STEPS.map((s, i) => (
              <li key={s.num} data-testid={`login-step-${i}`} className="card-premium p-6 flex gap-5">
                <div className="step-number w-12 h-12 rounded-xl flex items-center justify-center text-base flex-shrink-0">
                  {s.num}
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section-pad bg-[#070710] border-y border-white/5" data-testid="login-tips">
        <div className="shell grid lg:grid-cols-2 gap-8">
          <div className="card-premium p-7">
            <ShieldCheck className="w-8 h-8 text-[#10B981]" />
            <h3 className="mt-4 font-display text-2xl font-semibold">Tips Login Aman</h3>
            <ul className="mt-4 space-y-2.5">
              {TIPS.map((t, i) => (
                <li key={t} data-testid={`login-tip-${i}`} className="flex items-start gap-3 text-sm text-slate-300">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#10B981] flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-premium p-7">
            <AlertTriangle className="w-8 h-8 text-[#D4AF37]" />
            <h3 className="mt-4 font-display text-2xl font-semibold">Hal yang Perlu Dihindari</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-300">
              <li>• Jangan login melalui tautan dari pesan tidak dikenal.</li>
              <li>• Hindari mengakses akun dari jaringan publik yang tidak aman.</li>
              <li>• Jangan berbagi tangkapan layar yang menampilkan data sensitif.</li>
              <li>• Jangan memberi izin akses kepada aplikasi tidak resmi.</li>
            </ul>
            <Link to="/keamanan" data-testid="login-security-link" className="btn-secondary mt-6 inline-flex">
              Pelajari Keamanan Akun <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
